import React, { useState } from "react";
import { SearchFilter } from "@design-system/SearchFilter/SearchFilter";
import { KickLogo } from "@design-system/KickLogo/KickLogo";
import { SideNav } from "@design-system/SideNav/SideNav";
import { SideNavHeader } from "@design-system/SideNav/SideNavHeader";
import { SideNavItem } from "@design-system/SideNav/SideNavItem";
import { SideNavSection } from "@design-system/SideNav/SideNavSection";
import {
    designSystemEntryPath,
    playgroundComponents,
    PlaygroundComponentEntry,
    playgroundGroups,
} from "./playgroundComponents";
import { PlaygroundSlowMotionToggle } from "./PlaygroundSlowMotionToggle";
import styles from "./PlaygroundNavigation.module.scss";

const PlaygroundNavGroup = ({
    title,
    entries,
}: {
    title: string;
    entries: readonly PlaygroundComponentEntry[];
}) => {
    const [isOpen, setIsOpen] = useState(true);

    if (entries.length === 0) {
        return null;
    }

    return (
        <SideNavSection label={title} isOpen={isOpen} onOpenChange={setIsOpen}>
            {entries.map((entry) => (
                <SideNavItem
                    key={entry.id}
                    to={designSystemEntryPath(entry.id)}
                    icon={null}
                    label={entry.label}
                />
            ))}
        </SideNavSection>
    );
};

/** The design system playground's side nav: a search box and one section per group. */
export const PlaygroundNavigation: React.FC = () => {
    const [filter, setFilter] = useState<string | undefined>();
    const normalizedFilter = filter?.trim().toLowerCase();

    const filteredEntries = playgroundComponents.filter(
        (entry) =>
            !normalizedFilter ||
            entry.label.toLowerCase().includes(normalizedFilter),
    );

    return (
        <SideNav
            aria-label="Design system"
            header={
                <SideNavHeader
                    picker={
                        <span className={styles.title}>
                            <KickLogo variant="icon" size="sm" />
                            Design system
                        </span>
                    }
                />
            }
            top={
                <>
                    <SearchFilter
                        filter={filter}
                        onChange={setFilter}
                        minLength={1}
                        debounceTime={100}
                        placeholder="Search"
                        containerClassName={styles.searchFilter}
                    />
                    <PlaygroundSlowMotionToggle />
                </>
            }
        >
            {playgroundGroups.map((group) => (
                <PlaygroundNavGroup
                    key={group.id}
                    title={group.title}
                    entries={filteredEntries.filter(
                        (entry) => entry.group === group.id,
                    )}
                />
            ))}
        </SideNav>
    );
};
